<template>
    <!-- This card attaches to the user's message, whose grid is place-items-end; justify-self-start
         keeps it left-aligned and ml-2 matches the px-2 inset of the assistant-side cards so every
         chat card starts at the same x. -->
    <ChatAgentToolFrame is-actionable :is-complete="isSubmitted" :completion-message="completionMessage" class="ml-2 justify-self-start">
        <div v-if="!isSubmitted" class="flex flex-col gap-1" data-testid="chat-push-manager-review-to-workday">
            <div class="nadia-chat-bold text-ink">{{ t("chat.workdayReview.draftTitle", { title: reviewTitle }) }}</div>
            <div class="scaffold-details-small text-ink-muted">{{ t("chat.workdayReview.introLine1") }}<br />{{ t("chat.workdayReview.introLine2") }}</div>
        </div>

        <hr v-if="!isSubmitted" class="-mx-6 border-0 border-t border-border" aria-hidden="true" />

        <div v-if="!isSubmitted" ref="reviewFields" class="flex flex-col gap-5">
            <section v-for="section in draft.sections" :key="section.section_id" class="flex flex-col gap-2">
                <h4 class="body-small-semibold text-ink">{{ section.label }}</h4>
                <BaseTextarea
                    v-if="editing"
                    v-model:input="section.comment"
                    rows="3"
                    :disabled="loading"
                    :aria-label="t('chat.workdayReview.sectionCommentLabel', { label: section.label })"
                    :placeholder="t('chat.workdayReview.sectionPlaceholder', { label: section.label })"
                    class="field-textarea"
                    @blur="saveDraft" />
                <p v-else class="body-small text-ink-muted whitespace-pre-line">{{ section.comment || emptyPlaceholder }}</p>
            </section>

            <section class="flex flex-col gap-2">
                <h4 class="body-small-semibold text-ink">{{ t("chat.workdayReview.overallHeading") }}</h4>
                <BaseTextarea
                    v-if="editing"
                    v-model:input="draft.overall_comment"
                    rows="3"
                    :disabled="loading"
                    :aria-label="t('chat.workdayReview.overallHeading')"
                    :placeholder="t('chat.workdayReview.overallPlaceholder')"
                    class="field-textarea"
                    @blur="saveDraft" />
                <p v-else class="body-small text-ink-muted whitespace-pre-line">{{ draft.overall_comment || emptyPlaceholder }}</p>
            </section>
        </div>

        <!-- Status region, notice and buttons share one frame child so only a single gap sits above
             them, instead of the empty status region adding a second gap before the buttons. Margins
             (not flex gap) space the region and notice, so they contribute nothing while empty. -->
        <div v-if="!isSubmitted" class="flex flex-col">
            <!-- The region registers for as long as the card is unsubmitted, and only its text is
                 conditional. A region that appears already holding its message announces nothing.
                 Same shape as ChatAgentToolPushReviewToWorkday. -->
            <div class="body-small text-ink-muted" :class="{ 'mb-4': saving || hasUnsavedEdits }" aria-live="polite" aria-atomic="true">
                <span v-if="saving || hasUnsavedEdits">{{ saving ? t("chat.workdayReview.saving") : t("chat.workdayReview.editedNotice") }}</span>
            </div>
            <div v-if="noticeMessage" class="body-small text-notification-error-text mb-4" role="alert">{{ noticeMessage }}</div>

            <div class="flex flex-col gap-4">
                <BaseButton theme="neutral" data-testid="toggle-edit-manager-review" :disabled="loading" class="flex w-full items-center justify-center gap-1" @click="toggleEdit">
                    {{ editing ? t("chat.workdayReview.done") : t("common.edit") }}
                </BaseButton>

                <!-- Not disabled during draft saves: handleSubmit awaits the pending save, so a mid-save
                     click is safe and never races the version counter. -->
                <BaseButton :disabled="loading" class="flex w-full justify-center items-center gap-1" theme="primary" data-testid="submit-manager-review" @click="handleSubmit">
                    <LoadingSpinner v-if="loading" class="h-4" />{{ t("chat.workdayReview.submit") }}
                </BaseButton>
            </div>
        </div>
    </ChatAgentToolFrame>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, reactive, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";

const { t } = useI18n();

interface CardSection {
    section_id: string;
    label: string;
    comment: string;
}
interface CardDraft {
    review_ref: string;
    overall_comment: string;
    sections: CardSection[];
}

interface ActionResult {
    success?: boolean;
    payload?: {
        error?: string | null;
        draft_version?: number;
        draft?: CardDraft;
    };
}

interface Props {
    line: {
        action_params?: {
            draft?: CardDraft;
            reviewee_name?: string | null;
            integration_name?: string | null;
            draft_version?: number;
        };
        action_state?: {
            submitted?: boolean;
        } | null;
    };
    lineIdx: number;
    messageId: number;
}

const props = defineProps<Props>();

const { execute } = useActionHandler();
const updateActionState = inject<(payload: object) => void>("updateActionState")!;
const coachingSessionUuid = inject<string>("coachingSessionUuid")!;
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const emptyPlaceholder = t("chat.workdayReview.emptyPlaceholder");
const editing = ref(false);
const reviewFields = useTemplateRef<HTMLDivElement>("reviewFields");
const saving = ref(false);
const noticeMessage = ref("");

const isSubmitted = computed(() => Boolean(props.line.action_state?.submitted));
const revieweeName = computed(() => props.line.action_params?.reviewee_name || t("chat.workdayReview.revieweeFallback"));
const reviewTitle = computed(() => t("chat.workdayReview.reviewTitle", { name: revieweeName.value }));
const completionMessage = computed(() => t("chat.workdayReview.completionMessage", { name: revieweeName.value }));

function cloneDraft(): CardDraft {
    const source = props.line.action_params?.draft;
    const base: CardDraft = source ? JSON.parse(JSON.stringify(source)) : { review_ref: "", overall_comment: "", sections: [] };
    // Normalize so every editable field is a string bound to a textarea.
    base.overall_comment = base.overall_comment ?? "";
    base.sections = (base.sections ?? []).map((s) => ({
        ...s,
        comment: s.comment ?? "",
    }));
    return base;
}

const draft = reactive<CardDraft>(cloneDraft());
const draftVersion = ref(props.line.action_params?.draft_version ?? 0);

// The comment-only projection sent to the backend and used for dirty comparison. Identifiers are
// server-owned; only the comment text is client-authored.
function commentPayload() {
    return {
        overall_comment: draft.overall_comment,
        sections: draft.sections.map((section) => ({
            section_id: section.section_id,
            comment: section.comment,
        })),
    };
}

const savedSnapshot = ref(JSON.stringify(commentPayload()));

const hasUnsavedEdits = computed(() => JSON.stringify(commentPayload()) !== savedSnapshot.value);

// Serializes blur-saves and lets submit wait for an in-flight save, so a save's version bump can
// never race the submit's base_version.
let pendingSave: Promise<void> = Promise.resolve();

function adoptServerDraft(payload: { draft_version?: number; draft?: CardDraft }) {
    if (payload.draft) {
        draft.overall_comment = payload.draft.overall_comment ?? "";
        const incoming = new Map((payload.draft.sections ?? []).map((s) => [s.section_id, s]));
        for (const section of draft.sections) {
            const serverSection = incoming.get(section.section_id);
            if (!serverSection) {
                continue;
            }
            section.comment = serverSection.comment ?? "";
        }
    }
    if (payload.draft_version !== undefined) {
        draftVersion.value = payload.draft_version;
    }
    savedSnapshot.value = JSON.stringify(commentPayload());
    updateActionState({
        message_id: props.messageId,
        lineIdx: props.lineIdx,
        action_params: { draft: JSON.parse(JSON.stringify(draft)), draft_version: draftVersion.value },
    });
}

async function toggleEdit() {
    editing.value = !editing.value;
    if (!editing.value) {
        saveDraft();
    } else {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}edit_manager_review_draft`, { session_id: coachingSessionUuid }, coachingSessionUuid);
        await nextTick();
        reviewFields.value?.querySelector("textarea")?.focus();
    }
}

function saveDraft(): Promise<void> {
    if (!hasUnsavedEdits.value) {
        return pendingSave;
    }
    pendingSave = pendingSave.then(doSave);
    return pendingSave;
}

async function doSave() {
    if (!hasUnsavedEdits.value) {
        return;
    }
    saving.value = true;
    noticeMessage.value = "";
    try {
        const result: ActionResult = await execute(
            ChatAction.PUSH_MANAGER_REVIEW_TO_WORKDAY,
            {
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                subaction: "save_draft",
                base_version: draftVersion.value,
                action_params: commentPayload(),
            },
            { sync: true },
        );
        if (result?.success) {
            draftVersion.value = result.payload?.draft_version ?? draftVersion.value;
            savedSnapshot.value = JSON.stringify(commentPayload());
        } else {
            noticeMessage.value = result?.payload?.error ?? t("chat.workdayReview.saveError");
            if (result?.payload?.draft) {
                adoptServerDraft(result.payload);
            }
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = t("chat.workdayReview.saveError");
    } finally {
        saving.value = false;
    }
}

async function handleSubmit() {
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}push_manager_review_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        await pendingSave;
        const result: ActionResult = await execute(
            ChatAction.PUSH_MANAGER_REVIEW_TO_WORKDAY,
            {
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                base_version: draftVersion.value,
                action_params: commentPayload(),
            },
            { sync: true },
        );
        if (result?.success) {
            savedSnapshot.value = JSON.stringify(commentPayload());
            updateActionState({ message_id: props.messageId, lineIdx: props.lineIdx, action_state: { submitted: true } });
        } else {
            noticeMessage.value = result?.payload?.error ?? t("chat.workdayReview.submitError");
            if (result?.payload?.draft) {
                adoptServerDraft(result.payload);
            }
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = t("chat.workdayReview.submitError");
    } finally {
        stopLoading();
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .field-textarea {
        @apply w-full rounded-lg border border-control-border bg-control-bg px-4 py-3 text-control-text transition-colors focus-field;
    }
}
</style>
